/** Golden-angle hue steps keep hashed game names visually separated. */
const GOLDEN_ANGLE_DEG = 137.508;

/** Route slug → dashboard label (matches session journey `gameName` where possible). */
const SLUG_DISPLAY_NAMES: Record<string, string> = {
  spot: "Spot the Difference",
  fish: "Fishing",
  hunt: "Object Hunt",
  flick: "Flick",
  "blob-wars": "Blob Wars",
  foosball: "Foosball",
  quiz: "Quiz",
};

/** Compact wedge label (pie charts, tight UI). */
const SLUG_SHORT_NAMES: Record<string, string> = {
  spot: "Spot",
  fish: "Fish",
  hunt: "Hunt",
  flick: "Flick",
  "blob-wars": "Blob",
  foosball: "Foos",
  quiz: "Quiz",
};

/** Colours keyed by route slug and/or normalized display name (must stay in sync). */
const GAME_COLOR_OVERRIDES: Record<string, string> = {
  spot: "#d69b8d",
  "spot the difference": "#d69b8d",
  fish: "#1abc9c",
  fishing: "#1abc9c",
  hunt: "#9b59b6",
  "object hunt": "#9b59b6",
  flick: "#e056a0",
  "blob-wars": "#4f80c7",
  "blob wars": "#4f80c7",
  foosball: "#3d9a6e",
  quiz: "#c77b4f",
};

/**
 * Optional fixed colours for table display names (and `name · venue` labels).
 * Same pattern as GAME_COLOR_OVERRIDES — add a row when a fleet table needs a
 * memorable colour that the hash would otherwise make muddy or too similar.
 */
const TABLE_COLOR_OVERRIDES: Record<string, string> = {};

/** High-contrast cycle so fleet tables do not collapse into neighbouring greens. */
const DISTINCT_TABLE_COLORS = [
  "#3b82f6", // blue
  "#ef4444", // red
  "#eab308", // yellow
  "#a855f7", // purple
  "#14b8a6", // teal
  "#f97316", // orange
  "#ec4899", // pink
  "#22c55e", // green
  "#06b6d4", // cyan
  "#d946ef", // fuchsia
  "#84cc16", // lime
  "#f43f5e", // rose
] as const;

export const GAME_ABANDONED_COLOR = "#5a6b85";

export function normalizeGamePaletteKey(name: string): string {
  return name.trim().toLowerCase();
}

export function gameSlugFromConceptKey(conceptKey: string): string | null {
  const routeMatch = conceptKey.match(/\/games\/([^/]+)\/?/i);
  if (routeMatch?.[1]) {
    return routeMatch[1]
      .replace(/index\.html?$/i, "")
      .replaceAll("_", "-")
      .toLowerCase();
  }
  return null;
}

export function gameDisplayLabel(conceptKey: string): string {
  const slug = gameSlugFromConceptKey(conceptKey);
  if (slug && SLUG_DISPLAY_NAMES[slug]) return SLUG_DISPLAY_NAMES[slug]!;
  const raw = slug ?? conceptKey;
  const normalized = raw
    .replace(/index\.html?$/i, "")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .trim();
  if (!normalized) return conceptKey;
  return normalized
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** Short label for pie wedges and other tight chart slots. */
export function gameShortLabel(conceptKey: string): string {
  const slug = gameSlugFromConceptKey(conceptKey);
  if (slug && SLUG_SHORT_NAMES[slug]) return SLUG_SHORT_NAMES[slug]!;
  const display = gameDisplayLabel(conceptKey);
  if (display.length <= 10) return display;
  const first = display.split(/\s+/)[0];
  if (first && first.length <= 10) return first;
  return `${display.slice(0, 9)}.`;
}

/** Stable fill from a route key or display name (matches session journey when keyed by game). */
export function gameColorFromConceptKey(conceptKey: string): string {
  const slug = gameSlugFromConceptKey(conceptKey);
  if (slug) {
    const bySlug = GAME_COLOR_OVERRIDES[slug];
    if (bySlug) return bySlug;
  }
  return gameColorFromName(gameDisplayLabel(conceptKey));
}

function blendHexChannel(channel: number, towardWhite: number): number {
  return Math.round(channel + (255 - channel) * towardWhite);
}

/** Lighter companion for stacked “repeat” segments on the same playable. */
export function lighterGameColor(color: string, deltaLightness = 14): string {
  const hslMatch = color.match(/hsl\(([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\)/);
  if (hslMatch) {
    const l = Math.min(parseFloat(hslMatch[3]!) + deltaLightness, 78);
    return `hsl(${hslMatch[1]} ${hslMatch[2]}% ${l.toFixed(1)}%)`;
  }
  if (color.startsWith("#") && (color.length === 7 || color.length === 4)) {
    const hex =
      color.length === 4
        ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
        : color;
    const n = parseInt(hex.slice(1), 16);
    const r = blendHexChannel((n >> 16) & 0xff, 0.32);
    const g = blendHexChannel((n >> 8) & 0xff, 0.32);
    const b = blendHexChannel(n & 0xff, 0.32);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  }
  return color;
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0;
  return h;
}

/** Stable fill colour for a playable display name (session journey, charts, etc.). */
export function gameColorFromName(gameName: string): string {
  const key = normalizeGamePaletteKey(gameName);
  const override = GAME_COLOR_OVERRIDES[key];
  if (override) return override;
  const hue = (hashString(key) * GOLDEN_ANGLE_DEG) % 360;
  return `hsl(${hue.toFixed(1)} 58% 52%)`;
}

/**
 * Stable fill for a table label (prefer **table · venue**, else table name — not device UUID).
 * Overrides, else a fixed high-contrast cycle (like game overrides) so slices stay visually distinct.
 */
export function tableColorFromName(tableLabel: string): string {
  const key = normalizeGamePaletteKey(tableLabel);
  const override = TABLE_COLOR_OVERRIDES[key];
  if (override) return override;
  return DISTINCT_TABLE_COLORS[hashString(key) % DISTINCT_TABLE_COLORS.length]!;
}

export function shellScreenColor(screenName: string): string | null {
  if (screenName === "main_menu") return "#6b7280";
  if (screenName === "attract") return "#4b5563";
  return null;
}
